import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  CalendarDays,
  ClipboardList,
  Ticket,
  History,
  Star,
  CalendarCheck,
  BookOpen,
  FileText,
  Snowflake,
  ShoppingCart,
} from "lucide-react";
import ClubInfo from "../components/ClubInfo";
import SurveyBlock from "../components/SurveyBlock";
import { useHome } from "../hooks/useHome";
import AdvertisementComponent from "@/components/AdvertisementComponent";
import WelcomeMessageComponent from "@/components/WelcomeMessageComponent";
import { StoriesBar, StoryViewer } from "@/components/stories";
import { useStories } from "@/hooks/useStories";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";

const Home: React.FC = () => {
  const { userName } = useHome();
  const { authPhone } = useAuth();
  const { selectedClubId } = useClub();
  const {
    isVisible,
    isLoading: isStoriesLoading,
    storyGroups,
    viewedGroups,
    isViewerOpen,
    activeGroupIndex,
    openViewer,
    closeViewer,
    markAsViewed,
  } = useStories({ userPhone: authPhone, branchId: selectedClubId });

  const menuCards = [
    {
      icon: <ClipboardList className="h-6 w-6" />,
      title: "Мои занятия",
      description: "Просмотр ваших текущих и предстоящих занятий",
      link: "/classes",
      buttonText: "Перейти к занятиям",
    },
    {
      icon: <CalendarDays className="h-6 w-6" />,
      title: "Расписание",
      description: "Просмотр доступных занятий и запись на тренировки",
      link: "/sign-up",
      buttonText: "Перейти к расписанию",
    },
    {
      icon: <Ticket className="h-6 w-6" />,
      title: "Абонементы",
      description: "Информация о ваших активных абонементах",
      link: "/tickets",
      buttonText: "Перейти к абонементам",
    },
    {
      icon: <ShoppingCart className="h-6 w-6" />,
      title: "Купить абонемент",
      description: "Оформление и оплата нового абонемента",
      link: "/buy-ticket",
      buttonText: "Купить абонемент",
    },
    {
      icon: <History className="h-6 w-6" />,
      title: "Последние оплаты",
      description: "История ваших платежей и операций",
      link: "/purchase_history",
      buttonText: "Посмотреть историю",
    },
    {
      icon: <Star className="h-6 w-6" />,
      title: "Бонусный счёт",
      description: "Ваши накопленные бонусы и привилегии",
      link: "/bonuses",
      buttonText: "Проверить бонусы",
    },
    {
      icon: <CalendarCheck className="h-6 w-6" />,
      title: "История посещений",
      description: "Статистика ваших посещений занятий",
      link: "/visits_history",
      buttonText: "Посмотреть историю",
    },
    {
      icon: <BookOpen className="h-6 w-6" />,
      title: "Дневник пловца",
      description: "Отслеживание прогресса и достижений",
      link: "/achievements",
      buttonText: "Открыть дневник",
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: "Справки",
      description: "Управление медицинскими справками",
      link: "/certificates",
      buttonText: "Перейти к справкам",
    },
    {
      icon: <Snowflake className="h-6 w-6" />,
      title: "Заморозка",
      description: "Подать заявление на приостановку абонемента",
      link: "/freeze-application",
      buttonText: "Подать заявление",
    },
  ];

  return (
    <div className="mt-20 space-y-6 px-4 pb-6">
      {isVisible && (
        <>
          <StoriesBar
            storyGroups={storyGroups}
            viewedGroups={viewedGroups}
            onStoryClick={openViewer}
            isLoading={isStoriesLoading}
          />
          {isViewerOpen && (
            <StoryViewer
              storyGroups={storyGroups}
              initialGroupIndex={activeGroupIndex}
              onClose={closeViewer}
              onStoryViewed={markAsViewed}
            />
          )}
        </>
      )}
      <div className="rounded-lg bg-white">
        <WelcomeMessageComponent userName={userName} />
        <AdvertisementComponent />

        <div className="grid max-w-full grid-cols-1 gap-6 overflow-hidden md:hidden">
          {menuCards.map((card) => (
            <Link key={card.link} to={card.link} className="h-full">
              <Card className="h-full transition-all duration-200 hover:border-primary hover:shadow-md">
                <CardContent className="p-6">
                  <div className="mb-4 flex items-center gap-4">
                    <div className="rounded-lg bg-primary/10 p-3 text-primary">{card.icon}</div>
                    <h2 className="text-xl font-medium text-gray-900">{card.title}</h2>
                  </div>
                  <p className="mb-4 text-gray-600">{card.description}</p>
                  <Button className="w-full">{card.buttonText}</Button>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        <ClubInfo />
        <SurveyBlock />
      </div>
    </div>
  );
};

export default Home;
